import { useQuery } from '@tanstack/react-query';
import { productService } from '@/services/productService';

export interface PriceType {
  id: number;
  code: string;
  label: string;
}
export interface Currency {
  iso_code: string;
  symbol: string;
  name: string;
  decimals: number;
}

interface UsePriceMetadataOptions {
  enabled?: boolean;
}

export function usePriceMetadata(options: UsePriceMetadataOptions = {}) {
  const { enabled = true } = options;

  // Query for price types
  const {
    data: priceTypes = [],
    isLoading: isPriceTypesLoading,
    error: priceTypesError,
    refetch: refetchPriceTypes,
  } = useQuery<PriceType[]>({
    queryKey: ['priceTypes'],
    queryFn: async () => {
      const result = await productService.getPriceTypes();
      return Array.isArray(result) ? result : [];
    },
    enabled: enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Query for currencies
  const {
    data: currencies = [],
    isLoading: isCurrenciesLoading,
    error: currenciesError,
    refetch: refetchCurrencies,
  } = useQuery<Currency[]>({
    queryKey: ['currencies'],
    queryFn: async () => {
      const result = await productService.getCurrencies();
      return Array.isArray(result) ? result : [];
    },
    enabled: enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  return {
    priceTypes,
    currencies,
    loading: isPriceTypesLoading || isCurrenciesLoading,
    error: priceTypesError || currenciesError,
    refetch: () => {
      refetchPriceTypes();
      refetchCurrencies();
    },
  };
}
